// NirmalX pages style their own links (CSS modules), so they use next/link rather than the Chakra-styled Link.
// eslint-disable-next-line no-restricted-imports
import NextLink from 'next/link';
import React from 'react';

import type { Block } from 'types/api/block';
import type { HomeStats } from 'types/api/stats';

import { useCountUp } from '../hooks';
import { BLOCK_PERIOD_S, EMPTY_BLOCK_PERIOD_S, findValidator, shortHash, VALIDATORS } from '../nirmalx';
import css from './home.module.css';

const GAUGE_R = 54;
const GAUGE_C = 2 * Math.PI * GAUGE_R;

// Validator triangle, in % of the card: Alpha on top, Beta and Gamma below.
const V_POS = [ { x: 50, y: 24 }, { x: 18, y: 72 }, { x: 82, y: 72 } ];

function validatorNote(isLatest: boolean, lastHeight: number | undefined) {
  if (isLatest) {
    return 'Sealed the latest block';
  }
  return lastHeight ? `Last: #${ lastHeight.toLocaleString('en-US') }` : 'Waiting for its turn';
}

const Stat = ({ label, value, note }: { label: string; value: number | undefined; note: string }) => {
  const shown = useCountUp(value);
  return (
    <div className={ css.card }>
      <div className={ css.statLabel }>{ label }</div>
      <div className={ css.statValue }>{ value === undefined ? '—' : Math.round(shown).toLocaleString('en-US') }</div>
      <div className={ css.statNote }>{ note }</div>
    </div>
  );
};

interface Props {
  stats: HomeStats | undefined;
  blocks: Array<Block> | undefined;
  now: number;
}

const LiveDeck = ({ stats, blocks, now }: Props) => {
  const latest = blocks?.[0];
  const since = latest && now > 0 ? Math.max(0, Math.round((now - new Date(latest.timestamp).getTime()) / 1000)) : undefined;
  const progress = since === undefined ? 0 : Math.min(1, since / EMPTY_BLOCK_PERIOD_S);
  const proposer = findValidator(latest?.miner.hash);

  // The latest height each validator proposed, among the blocks on screen.
  const lastProposed = VALIDATORS.map((validator) => blocks?.find((block) => findValidator(block.miner.hash) === validator)?.height);

  return (
    <div className={ css.deck }>
      <Stat label="Blocks sealed" value={ stats ? Number(stats.total_blocks) : undefined } note="Since genesis"/>
      <Stat label="Transactions" value={ stats ? Number(stats.total_transactions) : undefined } note="All time"/>
      <Stat label="Wallet addresses" value={ stats ? Number(stats.total_addresses) : undefined } note="Seen on chain"/>
      <div className={ css.card }>
        <div className={ css.statLabel }>Block time</div>
        <div className={ css.statValue }>{ BLOCK_PERIOD_S } s</div>
        <div className={ css.statNote }>With transactions · one block every { EMPTY_BLOCK_PERIOD_S } s when idle</div>
      </div>

      <div className={ `${ css.card } ${ css.wide }` }>
        <div className={ css.statLabel }>Heartbeat</div>
        <div className={ css.heartbeat }>
          <div className={ css.ringGauge }>
            <svg viewBox="0 0 132 132" aria-hidden="true">
              <defs>
                <linearGradient id="nx-gauge" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#f0d876"/>
                  <stop offset="100%" stopColor="#d9520c"/>
                </linearGradient>
              </defs>
              <circle className={ css.gaugeTrack } cx="66" cy="66" r={ GAUGE_R }/>
              <circle
                className={ css.gaugeFill }
                cx="66"
                cy="66"
                r={ GAUGE_R }
                strokeDasharray={ GAUGE_C }
                strokeDashoffset={ GAUGE_C * (1 - progress) }
              />
            </svg>
            <div className={ css.gaugeText }>
              <b>{ since === undefined ? '—' : `${ since }s` }</b>
              <span>since block</span>
            </div>
          </div>
          <div className={ css.beatBody }>
            <svg className={ css.ecg } viewBox="0 0 260 46" preserveAspectRatio="none" aria-hidden="true">
              <path d="M0 24 H84 L94 10 L104 38 L116 4 L128 32 L136 24 H260"/>
            </svg>
            <div className={ css.beatTitle }>
              { latest ? (
                <>
                  Block{ ' ' }
                  <NextLink href={{ pathname: '/block/[height_or_hash]', query: { height_or_hash: String(latest.height) } }}>
                    #{ latest.height.toLocaleString('en-US') }
                  </NextLink>
                  { ' ' }by { proposer?.name ?? shortHash(latest.miner.hash) }
                </>
              ) : 'Waiting for the next block' }
            </div>
            <p className={ css.beatText }>
              Idle, the chain seals a heartbeat block every { EMPTY_BLOCK_PERIOD_S } s. Send a transaction and it lands in
              a block in about { BLOCK_PERIOD_S } s.
            </p>
          </div>
        </div>
      </div>

      <div className={ `${ css.card } ${ css.wide }` }>
        <div className={ css.statLabel }>QBFT validators</div>
        <div className={ css.statNote }>Round-robin proposers · a block is final once sealed</div>
        <div className={ css.validators }>
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            { V_POS.map((from, i) => {
              const to = V_POS[(i + 1) % V_POS.length];
              return <line key={ i } className={ css.qbftLine } x1={ from.x } y1={ from.y } x2={ to.x } y2={ to.y } vectorEffect="non-scaling-stroke"/>;
            }) }
          </svg>
          { VALIDATORS.map((validator, i) => (
            <div
              key={ validator.hash }
              className={ `${ css.vNode } ${ proposer === validator ? css.vActive : '' }` }
              style={{ left: `${ V_POS[i].x }%`, top: `${ V_POS[i].y }%` }}
            >
              <div className={ css.vDisc }>{ validator.tag }</div>
              <div className={ css.vName }>{ validator.name }</div>
              <div className={ css.vMeta }>{ validatorNote(proposer === validator, lastProposed[i]) }</div>
            </div>
          )) }
        </div>
      </div>
    </div>
  );
};

export default React.memo(LiveDeck);
